import * as React from 'react';
import styled, { useTheme } from 'styled-components/macro';
import {
  Field,
  Form,
  ErrorMessage,
  FieldProps,
  FormikErrors,
  FormikHelpers,
} from 'formik';
import { Button } from 'react-covideo-common';
import { RadioInput } from 'lib/components';
import { TextInput, EmailInput } from 'lib/components';
import { IoMdSave } from 'react-icons/io';
import { QUOTE_SETTINGS } from 'app/pages/repairOrders/components/RepairOrderListing';
import Switch from '../../../../design/landingPageBuilder/components/Switch';
import { Region, useAuth } from 'lib/context';
import {
  productFeature,
  checkIfFeatureIsEnabled,
  checkIfPreloadedTeleprompterScriptsAvailable,
} from 'lib/utils/productFeature';
import { MdHelp, MdLock } from 'react-icons/md';
import { ReactionPreferenceOptionsForCompany } from 'lib/const';
import { Heading } from 'lib/components/styles/typography';
import { Flex, HeaderWrapper } from 'lib/components/styles/layout';
import CreatableSelect from 'react-select/creatable';
import { creatableSelectStyle } from 'lib/style/commonStyles';
import { Option } from 'lib/components/dropdown/Dropdown';
import { MarkAsSentOptions } from 'lib/const/MarkAsSentOptions';
import { CompanyValues } from './companyValidator';
import selectors from '../../../../../../cypress/selectors';
import { NAPLETON_AUTOMOTIVE_RESELLER_ID } from 'lib/const/SuperAdminConstants';
import { Tooltip } from 'react-covideo-common';
import { theme } from 'lib/style';
import RouteLeavingGuard from 'app/pages/video/videoDetails/main/RouteLeavingGuard';
import { FormikRadioField } from 'lib/components/formik/FormikRadioField';
import { useCDK } from 'lib/hooks/useCdk';
import { useHistory } from 'react-router';
import { FormikImageUpload } from 'lib/components/formik/FormikImageUpload/FormikImageUpload';
import { FormikGoogleMaps } from 'lib/components/formik/FormikGoogleMaps/FormikGoogleMaps';
import { useTranslation } from 'react-i18next';
import { FormikDropdownField } from 'lib/components/formik/FormikDropdown';

interface FormProps {
  isSubmitting?: boolean;
  editCompanyName?: boolean;
  setFieldValue: any;
  values: any;
  isAutomotiveServiceRole: boolean;
  regions?: Region[];
  isAutomotive: boolean;
  dirty: boolean;
  isValid: boolean;
  errors: FormikErrors<CompanyValues>;
  resellerId: string;
  shareCooldownSeconds?: number;
  submitData: (
    values: CompanyValues,
    formik?: FormikHelpers<CompanyValues>
  ) => void;
}
interface SectionTitleProps {
  margin?: string;
}
const FlexForm = styled(Form)`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  .upgrade-button {
    display: flex;
    margin-left: auto;
  }
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: start;
  align-items: flex-start;
  width: 100%;
  padding: 0.5rem 0;

  :first-child {
    padding: 0 0 0.5rem 0;
  }

  @media screen and (max-width: 480px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const FieldContainer = styled.section`
  display: flex;
  width: 100%;
  flex-direction: column;
`;

const ErrorMsg = styled.p`
  color: red;
  font-size: 14px;
  padding: 0.1rem;
`;

const FieldLabel = styled.label`
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  width: 115px;
  color: rgba(146, 151, 162, 1);
  margin-right: 16px;
`;

const SectionTitle = styled.p<SectionTitleProps>`
  font-style: normal;
  font-weight: 600;
  font-size: 15px;
  margin: ${props => (props.margin ? props.margin : '0')};
  width: 100%;
  max-width: 200px;
  line-height: 24px;
`;

const SectionSubtitle = styled.p`
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: #9297a2;
  margin: 0 0 16px 0;
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  max-width: 700px;
  margin-left: 0;
  margin-top: 25px;
  margin-bottom: 25px;
  background-color: #eeeff2;
`;

const ToggleParagraph = styled.p`
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  margin: 0;
  line-height: 24px;
`;

const ToggleWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const RowWrapper = styled.div`
  width: 100%;
`;

const RadioInputWrapper = styled.div`
  display: flex;
  align-items: center;
  > span {
    font-style: normal;
    font-weight: 400;
    font-size: 16px;
    line-height: 24px;
    color: rgba(39, 42, 50, 1);
  }
`;

export const CompanyForm = (props: FormProps) => {
  const {
    editCompanyName = true,
    isAutomotiveServiceRole,
    isAutomotive,
    isSubmitting,
    setFieldValue,
    values,
    dirty,
    isValid,
    regions = [],
    resellerId,
    submitData,
  } = props;

  const { t } = useTranslation();

  const themes = useTheme();
  const history = useHistory();

  const [isReplyHidden, setIsReplyHidden] = React.useState(true);
  const [isTeleprompterHidden, setIsTeleprompterHidden] = React.useState(true);
  const { isCdkUser } = useCDK();
  const { userData, invalidateUser } = useAuth();

  const isLeadsIntegrationActive =
    (userData.customer?.leadsIntegrationActive === true ||
      userData.customer?.leadsIntegrationActive?.toString() === '1') &&
    !isCdkUser;

  const isAutominerCustomer =
    userData.customer.autominerEnabled?.toString() === '1';
  const regionOptions = userData.hasRegions
    ? regions.map((region: Region) => ({
        label: region.name,
        value: region.regionId,
      }))
    : [];

  React.useEffect(() => {
    const isReplyDisabled = !checkIfFeatureIsEnabled(
      userData,
      productFeature.VIDEO_REPLY
    );

    const isTelepromterDisabled = !checkIfFeatureIsEnabled(
      userData,
      productFeature.TELEPROMPTER
    );
    const isTeleprompterPreloadScriptsAvailable =
      checkIfPreloadedTeleprompterScriptsAvailable(userData);

    isReplyDisabled && setFieldValue('allowReplies', false);
    isTelepromterDisabled && setFieldValue('preloadTeleprompterScripts', false);

    setIsReplyHidden(isReplyDisabled);
    setIsTeleprompterHidden(
      isTelepromterDisabled || !isTeleprompterPreloadScriptsAvailable
    );
  }, []);

  const markVideosAsSentOptions = Object.values(MarkAsSentOptions).map(
    option => ({ value: option.value.toString(), label: t(option.label) })
  );

  return (
    <FlexForm>
      <RouteLeavingGuard
        when={true}
        onConfirm={async () => {
          await invalidateUser();
          await submitData(values);
        }}
        navigate={path => history.push(path)}
        shouldBlockNavigation={() => {
          return !!dirty;
        }}
      />
      <HeaderWrapper>
        <Heading>{t('Company Profile')}</Heading>
        <Button
          text={isSubmitting ? t('Saving') : t('Save changes')}
          type='submit'
          disabled={isSubmitting || !isValid || !dirty}
          variant='primary'
          icon={<IoMdSave />}
          data-cy={selectors.companyProfile.button.save}
        />
      </HeaderWrapper>
      {/* Business info */}
      <div style={{ display: 'flex', maxWidth: 700 }}>
        <SectionTitle>{t('Business info')}</SectionTitle>
        <RowWrapper>
          <Row>
            <FieldLabel>{t('Business Name')} *</FieldLabel>
            <Field name='businessName'>
              {({ field }: FieldProps) => {
                return (
                  <FieldContainer>
                    <TextInput
                      {...field}
                      type='text'
                      required={true}
                      readOnly={isSubmitting || !editCompanyName}
                      dataCy={selectors.companyProfile.field.business}
                      disabled={isCdkUser}
                    />
                    <ErrorMessage name='businessName' component={ErrorMsg} />
                  </FieldContainer>
                );
              }}
            </Field>
          </Row>
          {userData.hasRegions && (
            <Row>
              <FieldLabel>{t('Region')}</FieldLabel>
              <Field name='region'>
                {({ field }: FieldProps) => {
                  return (
                    <FieldContainer>
                      <CreatableSelect
                        {...field}
                        isDisabled={isSubmitting}
                        components={{
                          IndicatorSeparator: () => {
                            return null;
                          },
                        }}
                        formatCreateLabel={() => <></>}
                        styles={{
                          ...creatableSelectStyle,
                          indicatorsContainer: (base: any) => ({
                            ...base,
                            cursor: 'pointer',
                          }),
                          valueContainer: (base: any) => ({
                            ...base,
                            overflow: 'unset',
                          }),
                        }}
                        placeholder={t('Add region...')}
                        isMulti={false}
                        noOptionsMessage={() => null}
                        isClearable={true}
                        isSearchable={false}
                        options={regionOptions}
                        value={regionOptions.find(
                          (opt: Option) =>
                            opt?.value?.toString() ===
                            values?.region?.value?.toString()
                        )}
                        onChange={(opt: any) => {
                          setFieldValue('region', {
                            value: Number(opt?.value) || 0,
                            label: opt?.label || '',
                          });
                        }}
                      />
                    </FieldContainer>
                  );
                }}
              </Field>
            </Row>
          )}
          <Row>
            <FieldLabel>{t('First Name')} *</FieldLabel>
            <Field name='firstNameBusiness' readOnly={isSubmitting}>
              {({ field }: FieldProps) => {
                return (
                  <FieldContainer>
                    <TextInput {...field} type='text' required={true} />
                    <ErrorMessage
                      name='firstNameBusiness'
                      component={ErrorMsg}
                    />
                  </FieldContainer>
                );
              }}
            </Field>
          </Row>
          <Row>
            <FieldLabel>
              {t('Last Name')}{' '}
              {Number(resellerId) !== NAPLETON_AUTOMOTIVE_RESELLER_ID
                ? '*'
                : ''}
            </FieldLabel>
            <Field name='lastNameBusiness' readOnly={isSubmitting}>
              {({ field }: FieldProps) => {
                return (
                  <FieldContainer>
                    <TextInput
                      {...field}
                      type='text'
                      required={
                        Number(resellerId) !== NAPLETON_AUTOMOTIVE_RESELLER_ID
                      }
                    />
                    <ErrorMessage
                      name='lastNameBusiness'
                      component={ErrorMsg}
                    />
                  </FieldContainer>
                );
              }}
            </Field>
          </Row>
          <Row>
            <FieldLabel>{t('Email')} *</FieldLabel>
            <Field name='businessEmail' readOnly={isSubmitting}>
              {({ field }: FieldProps) => {
                return (
                  <FieldContainer>
                    <EmailInput {...field} type='email' />
                    <ErrorMessage name='businessEmail' component={ErrorMsg} />
                  </FieldContainer>
                );
              }}
            </Field>
          </Row>
          <Row>
            <FieldLabel>Logo</FieldLabel>
            <FormikImageUpload
              name='customerLogo'
              text='Upload Logo'
              extendStyles={{
                containerStyles: { height: '120px' },
                placeholderStyles: { fontSize: '14px' },
              }}
              disabled={isSubmitting}
              targetWidth={500}
            />
          </Row>
        </RowWrapper>
      </div>
      <Divider />
      {/* Contact info */}
      <div style={{ display: 'flex', maxWidth: 700 }}>
        <SectionTitle>{t('Contact')}</SectionTitle>
        <RowWrapper>
          <Row>
            <FieldLabel>{t('Phone 1')} *</FieldLabel>
            <Field name='businessPhone1' readOnly={isSubmitting}>
              {({ field }: FieldProps) => {
                return (
                  <FieldContainer>
                    <TextInput {...field} type='text' required={true} />
                    <ErrorMessage name='businessPhone1' component={ErrorMsg} />
                  </FieldContainer>
                );
              }}
            </Field>
          </Row>
          <Row>
            <FieldLabel>{t('Phone 2')}</FieldLabel>
            <Field name='businessPhone2' readOnly={isSubmitting}>
              {({ field }: FieldProps) => {
                return (
                  <FieldContainer>
                    <TextInput {...field} type='text' />
                    <ErrorMessage name='businessPhone2' component={ErrorMsg} />
                  </FieldContainer>
                );
              }}
            </Field>
          </Row>
          <Row>
            <FieldLabel>{t('Fax')}</FieldLabel>
            <Field name='businessFax' readOnly={isSubmitting}>
              {({ field }: FieldProps) => {
                return (
                  <FieldContainer>
                    <TextInput {...field} type='text' />
                  </FieldContainer>
                );
              }}
            </Field>
          </Row>
          <Row>
            <FieldLabel>{t('URL')}</FieldLabel>
            <Field
              name='businessUrl'
              readOnly={isSubmitting}
              placeholder='ex: http://www.mywebsite.com'
            >
              {({ field }: FieldProps) => {
                return (
                  <FieldContainer>
                    <TextInput {...field} type='text' />
                    <ErrorMessage name='businessUrl' component={ErrorMsg} />
                  </FieldContainer>
                );
              }}
            </Field>
          </Row>
          <Row>
            <FieldLabel>Google Map</FieldLabel>
            <FormikGoogleMaps name='mapIframeEmbedCode' />
          </Row>
        </RowWrapper>
      </div>
      <Divider />
      <div style={{ display: 'flex', maxWidth: 700 }}>
        <SectionTitle>{t('Video Reply')}</SectionTitle>
        <RowWrapper>
          {!isReplyHidden && (
            <>
              <Field name='allowReplies' readonly={isSubmitting}>
                {() => {
                  return (
                    <ToggleWrapper>
                      <ToggleParagraph>
                        {t('Enable Video Reply')}
                      </ToggleParagraph>
                      <Switch
                        id={'switch-video-reply'}
                        isOn={!!values.allowReplies}
                        onColor={themes.colors.primary[100]}
                        handleToggle={() => {
                          setFieldValue(`allowReplies`, !values.allowReplies);
                        }}
                      />
                    </ToggleWrapper>
                  );
                }}
              </Field>
            </>
          )}
          {isReplyHidden && (
            <Button
              text={t('Upgrade to Pro')}
              disabled={isSubmitting}
              className={'upgrade-button'}
              variant='white'
              icon={<MdLock />}
              onClick={() => (window.location.href = '/upgrade-plan')}
            />
          )}
        </RowWrapper>
      </div>

      <Divider />

      {!isTeleprompterHidden && !isCdkUser && (
        <>
          <div style={{ display: 'flex', maxWidth: 700 }}>
            <SectionTitle>{t('Teleprompter Scripts')}</SectionTitle>
            <RowWrapper>
              <>
                <Field
                  name='preloadTeleprompterScripts'
                  readonly={isSubmitting}
                >
                  {() => {
                    return (
                      <ToggleWrapper>
                        <ToggleParagraph>
                          {t('Preload Teleprompter Scripts')}
                        </ToggleParagraph>
                        <Switch
                          id={'switch-preloaded-scripts'}
                          isOn={!!values.preloadTeleprompterScripts}
                          onColor={themes.colors.primary[100]}
                          handleToggle={() => {
                            setFieldValue(
                              `preloadTeleprompterScripts`,
                              !values.preloadTeleprompterScripts
                            );
                          }}
                        />
                      </ToggleWrapper>
                    );
                  }}
                </Field>
              </>
            </RowWrapper>
          </div>
          <Divider />
        </>
      )}

      {!isCdkUser && (
        <>
          <Flex flexDirection='row' maxWidth={'700px'} margin={'16px 0 0 0'}>
            <SectionTitle>{t('Reactions & Comments')}</SectionTitle>
            <Flex flexDirection='column' alignItems='flex-start' gap='10px'>
              <FormikRadioField
                name='reactionsPreference'
                label={t('Disable for all users')}
                value={ReactionPreferenceOptionsForCompany.ALL_USERS.toString()}
              />
              <FormikRadioField
                name='reactionsPreference'
                label={t('Enable and turn on by default')}
                value={ReactionPreferenceOptionsForCompany.TURN_ON_DEFAULT.toString()}
              />
              <FormikRadioField
                name='reactionsPreference'
                label={t('Enable and turn off by default')}
                value={ReactionPreferenceOptionsForCompany.TURN_OFF_DEFAULT.toString()}
              />
            </Flex>
          </Flex>
          <Divider />
        </>
      )}

      {isAutomotiveServiceRole && (
        <div style={{ display: 'flex', maxWidth: 700 }}>
          <SectionTitle>{t('Quote settings')}</SectionTitle>
          <RowWrapper>
            <SectionSubtitle>
              {t('Calculate close ratio based on')}:
            </SectionSubtitle>
            <RadioInputWrapper>
              <RadioInput
                checked={values.quoteSettings === QUOTE_SETTINGS.DOLLAR}
                name='quoteSettings'
                value={QUOTE_SETTINGS.DOLLAR}
                onChange={() =>
                  setFieldValue('quoteSettings', QUOTE_SETTINGS.DOLLAR)
                }
                onClick={() =>
                  setFieldValue('quoteSettings', QUOTE_SETTINGS.DOLLAR)
                }
              />
              <span>{t('dollar amount')}</span>
            </RadioInputWrapper>
            <RadioInputWrapper>
              <RadioInput
                checked={values.quoteSettings === QUOTE_SETTINGS.ITEM}
                name='quoteSettings'
                value='item'
                onClick={() =>
                  setFieldValue('quoteSettings', QUOTE_SETTINGS.ITEM)
                }
                onChange={() =>
                  setFieldValue('quoteSettings', QUOTE_SETTINGS.ITEM)
                }
              />
              <span>{t('line items')}</span>
            </RadioInputWrapper>
          </RowWrapper>
        </div>
      )}

      {isAutomotive && (
        <div style={{ display: 'flex', maxWidth: 700 }}>
          <Tooltip
            extendStyles={{
              element: {
                width: '100%',
                maxWidth: '200px',
              },
              popup: {
                padding: '10px',
              },
            }}
            popup={<>{t('Mark videos as “Sent” when video link is copied.')}</>}
          >
            <SectionTitle>
              {t("Mark 'Sent' on Copy")}{' '}
              <MdHelp color={theme.palette.midGrey} />
            </SectionTitle>
          </Tooltip>
          <RowWrapper>
            <FormikDropdownField
              options={markVideosAsSentOptions}
              name='markVideosAsSent'
            />
          </RowWrapper>
        </div>
      )}

      {isAutomotive && (
        <div style={{ display: 'flex', maxWidth: 700 }}>
          <Tooltip
            extendStyles={{
              element: { width: '100%', maxWidth: '200px' },
              popup: { padding: '10px', maxWidth: '300px' },
            }}
            popup={
              <>
                {t(
                  'Time in seconds users must wait before generating a new share link. During cooldown they can still copy the latest generated link. Set to 0 to disable.'
                )}
              </>
            }
          >
            <SectionTitle>
              {t('Share URL cooldown')} <MdHelp color={theme.palette.midGrey} />
            </SectionTitle>
          </Tooltip>
          <RowWrapper>
            <Field name='shareCooldownSeconds' readonly={isSubmitting}>
              {({ field }: FieldProps) => {
                return (
                  <FieldContainer>
                    <TextInput
                      {...field}
                      type='number'
                      required={false}
                      readOnly={isSubmitting}
                    />
                  </FieldContainer>
                );
              }}
            </Field>
          </RowWrapper>
        </div>
      )}

      {isAutomotive && !isCdkUser && (
        <>
          <Divider />
          <div style={{ display: 'flex', maxWidth: 700, marginBottom: '20px' }}>
            <SectionTitle margin='auto'>{t('700Credit URL')}</SectionTitle>
            <RowWrapper>
              <Field name='credit700Url' readonly={isSubmitting}>
                {({ field }: FieldProps) => {
                  return (
                    <FieldContainer>
                      <TextInput
                        {...field}
                        type='text'
                        required={false}
                        readOnly={isSubmitting || !editCompanyName}
                      />
                    </FieldContainer>
                  );
                }}
              </Field>
            </RowWrapper>
          </div>
        </>
      )}

      {isAutominerCustomer && (
        <>
          <div style={{ display: 'flex', maxWidth: 700 }}>
            <SectionTitle margin='auto'>{t('Autominer Group ID')}</SectionTitle>
            <RowWrapper>
              <Field name='autominerGroupId' readonly={isSubmitting}>
                {({ field }: FieldProps) => {
                  return (
                    <FieldContainer>
                      <TextInput {...field} type='text' required={false} />
                    </FieldContainer>
                  );
                }}
              </Field>
            </RowWrapper>
          </div>
          <Divider />
        </>
      )}

      {isLeadsIntegrationActive && (
        <>
          <div style={{ display: 'flex', maxWidth: 700 }}>
            <Tooltip
              extendStyles={{
                element: {
                  width: '100%',
                  maxWidth: '200px',
                },
                popup: {
                  padding: '10px',
                },
              }}
              popup={
                <>
                  {t('Let users manage their Lead Notifications preferences.')}
                </>
              }
            >
              <SectionTitle>
                {t('Leads Notification')}{' '}
                <MdHelp color={theme.palette.midGrey} />
              </SectionTitle>
            </Tooltip>{' '}
            <RowWrapper>
              <>
                <Field name='leadsNotificationsAccess' readonly={isSubmitting}>
                  {() => {
                    return (
                      <ToggleWrapper>
                        <ToggleParagraph>
                          Enable Lead Notifications
                        </ToggleParagraph>
                        <Switch
                          id={'switch-lead-notificaotin-access'}
                          isOn={!!values.leadsNotificationsAccess}
                          onColor={themes.colors.primary[100]}
                          handleToggle={() => {
                            setFieldValue(
                              `leadsNotificationsAccess`,
                              !values.leadsNotificationsAccess
                            );
                          }}
                        />
                      </ToggleWrapper>
                    );
                  }}
                </Field>
              </>
            </RowWrapper>
          </div>
          <Divider />
        </>
      )}
    </FlexForm>
  );
};
